import { FreightGroupKey } from "./outboundPlanningGridUtils";

export type ShipmentListCategory = FreightGroupKey | "pickupsAggregate" | "all";

export interface ShipmentListContext {
  category: ShipmentListCategory;
  destinationTerminalNumbers: number[];
  destinationTerminalName?: string;
  destinationTerminalAirportCode?: string;
  hubName?: string;
  /** Unique per drawer open — busts list query cache so each open refetches */
  fetchId: string;
}

export function createShipmentListFetchId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const CATEGORY_LABELS: Record<ShipmentListCategory, string> = {
  inHouse: "In-House Freight",
  inbound: "Inbound Freight",
  pickupsAggregate: "Pickups",
  pickupsUnassigned: "Pickups — Unassigned",
  pickupsAssigned: "Pickups — Assigned",
  pickupsCompleted: "Pickups — Completed",
  all: "All Shipments",
};

function formatTerminalCountLabel(count: number): string {
  return `${count} terminal${count === 1 ? "" : "s"}`;
}

export function getShipmentListDrawerTitle(context: ShipmentListContext): string {
  const categoryLabel = CATEGORY_LABELS[context.category];

  if (context.hubName) {
    return `${categoryLabel} — ${context.hubName} (${formatTerminalCountLabel(context.destinationTerminalNumbers.length)})`;
  }

  const terminalNumber = context.destinationTerminalNumbers[0];
  const terminalLabel = context.destinationTerminalAirportCode
    ? `${context.destinationTerminalAirportCode} - ${context.destinationTerminalName} (${terminalNumber})`
    : `${context.destinationTerminalName} (${terminalNumber})`;
  return `${categoryLabel} — ${terminalLabel}`;
}

export function isFreightListCategory(
  category: ShipmentListCategory
): category is "inHouse" | "inbound" {
  return category === "inHouse" || category === "inbound";
}

export const PICKUP_FREIGHT_TYPES: Record<
  Extract<ShipmentListCategory, FreightGroupKey | "pickupsAggregate" | "all">,
  string[]
> = {
  pickupsAggregate: ["UNASSIGNED_PICKUP", "ASSIGNED_PICKUP", "COMPLETED_PICKUP"],
  pickupsUnassigned: ["UNASSIGNED_PICKUP"],
  pickupsAssigned: ["ASSIGNED_PICKUP"],
  pickupsCompleted: ["COMPLETED_PICKUP"],
  inHouse: [],
  inbound: [],
  all: [],
};
